import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  Wrench,
  ArrowLeft,
  Calendar,
  MapPin,
  Camera,
  FileText,
  Star,
  Loader2,
  Download,
  AlertCircle,
  CheckCircle2,
  Eye,
  User as UserIcon,
  Users as UsersIcon,
} from "lucide-react";
import api from "../lib/api";
import PhaseChip from "../components/PhaseChip";
import { JCProgressBar } from "../components/JCProgress";
import { uploadUrl } from "../lib/uploads";
import useServerPdf from "../lib/useServerPdf";
import ReadAloudButton from "../components/ReadAloudButton";
import ExpandableText from "../components/ExpandableText";
import { useAuth } from "../context/AuthContext";
import MediaLightbox from "../components/MediaLightbox";
import SignaturePad from "../components/SignaturePad";
import { toast } from "../components/ui/sonner";
import DictTextarea from "../components/DictTextarea";

/**
 * /portal/jobcards/:jid — Wave 26.2.
 *
 * Full-page customer-facing JC detail. Replaces the popup modal that
 * was rejected by the design contract ("never use tiny pop-up screens
 * — always open the full view").
 *
 * Renders the **same** scrollable detail experience the staff page
 * provides (header → quick facts → tasks → photos → documents → team
 * → ratings → actions footer), but the data is fetched from
 * `GET /api/portal/jobcards/{jid}` which strips every internal field
 * server-side (invoice notes, costs, hours, internal photos/comments,
 * etc — see backend `_CLIENT_HIDDEN_JC_FIELDS`).
 *
 * Routing:
 *   /portal/jobcards/:jid              — real customer (role=client) or owner self-link
 *   ?customer_id=<cid> query param     — staff in preview mode
 *
 * The "Preview as <Customer>" banner appears when the requester is
 * staff so owners are never confused about which lens they're in.
 */
export default function PortalJobCardDetail() {
  const { jid } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const previewCustomerId = search.get("customer_id");
  const isStaffPreview = !!user && user.role !== "client";

  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [team, setTeam] = useState([]); // hydrated [{ id, name, role }]
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const shortId = job?.short_id || job?.id?.slice(0, 8) || "jobcard";
  const { download: downloadPdf, busy: downloadingPdf } = useServerPdf({
    endpoint: jid ? `/jobcards/${jid}/pdf?as_client=1` : null,
    filename: `JobCard_${shortId}.pdf`,
    // [Iter 290zp.32] Portal (customer-facing) MUST NEVER silently
    // fall back to `window.print()` on a server error. That produces
    // a browser web-print of the React DOM (URL + page counter in
    // header/footer, no branding, wrong template) — the exact PDF
    // the user attached in the bug report. If the WeasyPrint call
    // errors, useServerPdf already surfaces a proper toast. Let the
    // customer retry instead of silently downgrading their document.
    fallbackPrint: false,
  });

  useEffect(() => {
    if (!jid) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    const params = previewCustomerId ? { customer_id: previewCustomerId } : {};

    (async () => {
      try {
        const { data } = await api.get(`/portal/jobcards/${jid}`, { params });
        if (cancelled) return;
        setJob(data);
        // Hydrate customer + site for header context (read-only — staff
        // can fetch via the regular routes; real clients have a portal
        // /me endpoint scoped to themselves).
        if (data.customer_id) {
          api
            .get(`/customers/${data.customer_id}`)
            .then((r) => !cancelled && setCustomer(r.data))
            .catch(() => {});
        }
        if (data.site_id) {
          api
            .get(`/sites`)
            .then((r) => {
              if (cancelled) return;
              setSite((r.data || []).find((s) => s.id === data.site_id));
            })
            .catch(() => {});
        }
        const ids = Array.from(
          new Set([data.technician_id, ...(data.staff_ids || [])].filter(Boolean)),
        );
        if (ids.length) {
          api
            .get(`/users`)
            .then((r) => {
              if (cancelled) return;
              const byId = new Map((r.data || []).map((u) => [u.id, u]));
              setTeam(ids.map((id) => byId.get(id)).filter(Boolean));
            })
            .catch(() => {});
        }
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 404) setErr("This job is no longer available.");
        else if (code === 403) setErr("You don't have access to this job.");
        else setErr("Could not load this job. Try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jid, previewCustomerId]);

  const photoSrc = (p) => {
    const url = typeof p === "string" ? p : p?.url || p?.path;
    if (!url) return "";
    // [Iter 327] Delegate to smart `uploadUrl()` — handles bare id,
    // path, and stale-token full URLs uniformly.
    if (url.startsWith("data:")) return url;
    return uploadUrl(url) || url;
  };

  const visiblePhotos = (job?.photos || []).filter(Boolean);
  const visibleDocs = (job?.documents || []).filter(Boolean);
  const checklist = job?.checklist || [];
  // [Iter 345 — 2026-02-25] In-portal image lightbox.
  // Previously `<a href={src} target="_blank">` navigated the PWA to
  // the raw upload URL — which loaded a JSON error (no auth) or, on
  // iOS PWA, dumped the user out of the portal shell entirely. On
  // return the app landed in CRM (impersonation lost). Fixed by
  // keeping every image view inside the portal — no navigation, no
  // shell break.
  //
  // Iter 363 (2026-02-27) — Dirk: "Clicking on an inline task photo
  // has to open the photo, same as the normal gallery does."
  // Task-inline photos now flow into the same lightbox queue with a
  // "Task 3 · Photo 1" caption so click-through + close + arrow keys
  // work identically to the JC-level gallery.
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const jcLightboxItems = visiblePhotos.map((p, i) => ({
    src: photoSrc(p),
    kind: "image",
    caption: p?.caption || `Photo ${i + 1}`,
  }));
  const taskLightboxItems = [];
  const taskPhotoLightboxIndex = {};   // {taskIdx: {photoIdx: lightboxIdx}}
  checklist.forEach((c, tIdx) => {
    const photos = Array.isArray(c.photos) ? c.photos.filter(Boolean) : [];
    if (!photos.length) return;
    taskPhotoLightboxIndex[tIdx] = {};
    photos.forEach((p, pIdx) => {
      taskPhotoLightboxIndex[tIdx][pIdx] = jcLightboxItems.length + taskLightboxItems.length;
      taskLightboxItems.push({
        src: photoSrc(p),
        kind: "image",
        caption: `Task ${tIdx + 1} · Photo ${pIdx + 1}`,
      });
    });
  });
  const lightboxItems = jcLightboxItems.concat(taskLightboxItems);

  // [Iter 345] Auth-carrying blob download for portal documents. The
  // old `<a href="..." target="_blank">` fired an unauth'd request
  // that returned JSON, so iOS Safari opened a JSON payload as
  // "document" and the user saw a blank page. Now we fetch through
  // the axios interceptor (Bearer + impersonation cookie both wired
  // in), assemble a Blob, and drive the browser's native save. Stays
  // inside the portal shell.
  const downloadPortalDoc = async (d) => {
    const url = d.url || d.path || "";
    if (!url) {
      toast.error("Document has no URL.");
      return;
    }
    try {
      // Absolute URLs (data:, http:) → fetch directly. Bare fids/paths → api.get.
      let blob;
      let filename = d.label || d.filename || d.name || "document";
      if (url.startsWith("data:") || url.startsWith("http")) {
        const resp = await fetch(url, { credentials: "include" });
        blob = await resp.blob();
      } else {
        // uploadUrl() returns "/api/uploads/{fid}" — strip the /api
        // prefix because the api instance already prepends it.
        const path = url.startsWith("/api/") ? url.slice(4) : (url.startsWith("/") ? url : `/uploads/${url}`);
        const resp = await api.get(path, { responseType: "blob" });
        blob = resp.data;
      }
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 15_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Download failed.");
    }
  };
  const back = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(isStaffPreview && previewCustomerId ? `/portal/preview/${previewCustomerId}` : "/portal");
  };

  return (
    <div className="min-h-screen bg-[#0a0b0e] text-white" data-testid="portal-jc-detail">
      {/* Preview banner — only when staff is impersonating a customer */}
      {isStaffPreview && (
        <div
          className="bg-amber-500/10 border-b border-amber-500/30 text-amber-200 text-xs py-2 px-4 flex items-center gap-2"
          data-testid="portal-jc-preview-banner"
        >
          <Eye className="w-3.5 h-3.5" />
          <span>
            <strong>Preview mode</strong> — you're seeing what{" "}
            <strong>{customer?.name || "the customer"}</strong> sees. Internal
            notes, costs, hours and staff comments are hidden.
          </span>
        </div>
      )}

      {/* Top bar */}
      <div className="border-b border-white/5 bg-[#0a0b0e]/80 backdrop-blur sticky top-0 z-20" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={back}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="portal-jc-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087]">
              Job card · {customer?.name || "—"}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {job?.short_id && (
                <span className="font-mono text-xs text-[#1FB6C1]">#{job.short_id}</span>
              )}
              <h1 className="text-lg sm:text-xl font-display font-bold leading-tight truncate">
                {job?.title || (loading ? "Loading…" : "—")}
              </h1>
              {job?.phase && <PhaseChip value={job.phase} />}
              {job?.title && (
                <ReadAloudButton
                  text={job.title}
                  size="xs"
                  testId="portal-jc-title-tts"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading && (
          <div className="py-16 text-center text-sm text-[#9aa3b5]">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading job…
          </div>
        )}
        {err && !loading && (
          <div className="py-12 text-center text-sm text-rose-300 flex flex-col items-center gap-2">
            <AlertCircle className="w-6 h-6" /> {err}
          </div>
        )}
        {!loading && !err && job && (
          <div className="space-y-6">
            {/* Quick facts */}
            <div className="grid sm:grid-cols-3 gap-3">
              {job.scheduled_at && (
                <FactCard
                  icon={Calendar}
                  label="Scheduled"
                  value={new Date(job.scheduled_at).toLocaleString("en-GB", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                />
              )}
              <FactCard
                icon={MapPin}
                label="Site"
                value={site?.name || site?.address || "—"}
              />
              {Array.isArray(checklist) && checklist.length > 0 && (
                <FactCard
                  icon={Wrench}
                  label="Progress"
                  value={
                    <div className="mt-0.5">
                      <JCProgressBar tasks={checklist} compact />
                    </div>
                  }
                />
              )}
            </div>

            {/* Iter 424.e — Job Report at the TOP of the portal JC page
                (before Brief and Tasks). Preserves newlines and bullets. */}
            {(job.job_report || "").trim() && (
              <Section
                title="Job Report"
                action={<ReadAloudButton text={job.job_report} testId="portal-jc-report-tts" />}
              >
                <div
                  className="text-sm text-[#cbd5e1] whitespace-pre-wrap"
                  data-testid="portal-jc-report"
                >
                  {job.job_report}
                </div>
              </Section>
            )}

            {/* Brief / description */}
            {(job.description || job.brief) && (
              <Section
                title="Brief"
                action={<ReadAloudButton text={job.description || job.brief} testId="portal-jc-brief-tts" />}
              >
                <ExpandableText
                  text={job.description || job.brief}
                  lines={6}
                  testId="portal-jc-brief"
                />
              </Section>
            )}

            {/* Team — Wave 26.2 — customer is allowed to know who serviced them */}
            {team.length > 0 && (
              <Section title="Your team">
                <div className="flex flex-wrap gap-3">
                  {team.map((m, i) => (
                    <div
                      key={m.id}
                      className="flex items-center gap-2 px-3 py-2 rounded border border-white/5 bg-white/[0.02]"
                      data-testid={`portal-jc-team-${m.id}`}
                    >
                      {i === 0 ? (
                        <UserIcon className="w-3.5 h-3.5 text-[#1FB6C1]" />
                      ) : (
                        <UsersIcon className="w-3.5 h-3.5 text-[#647087]" />
                      )}
                      <div className="text-sm">
                        <div className="font-medium leading-none">{m.name}</div>
                        <div className="text-[10px] text-[#647087] mt-0.5">
                          {i === 0 ? "Lead technician" : (m.role || "Team")}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Tasks */}
            {checklist.length > 0 && (
              <Section title={`Tasks (${checklist.length})`}>
                <div className="space-y-2">
                  {checklist.map((c, idx) => {
                    const status = (c.status || "pending").toLowerCase();
                    const ok = status === "done";
                    return (
                      <div
                        key={c.id || c.label}
                        className="rounded border border-white/5 bg-white/[0.02] p-3"
                        data-testid={`portal-jc-task-${c.id}`}
                      >
                        <div className="flex items-start gap-2">
                          {/* [Iter 331] Task number. Same shape as staff
                              view — sequential, unambiguous reference. */}
                          <span
                            className={`shrink-0 select-none inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 rounded-md text-[11px] font-mono tabular-nums font-semibold border ${
                              ok
                                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                                : status === "in_progress"
                                ? "border-amber-400/40 bg-amber-400/10 text-amber-300"
                                : "border-white/10 bg-white/5 text-[#a0aabf]"
                            }`}
                            data-testid={`portal-task-num-${c.id || idx}`}
                            aria-label={`Task ${idx + 1}`}
                          >
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="font-medium whitespace-pre-wrap">
                                {c.label || c.title || "—"}
                              </div>
                              <span className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">
                                {status.replace("_", " ")}
                              </span>
                            </div>
                            {c.description && (
                              <div className="text-[12px] text-[#9aa3b5] mt-1">
                                {c.description}
                              </div>
                            )}
                            {/* Iter 363 (2026-02-27) — Task-inline
                                photos with click-through into the
                                same portal lightbox used by the
                                JC-level gallery. Filters out any
                                comment/photo already stripped by the
                                server-side scrubber. */}
                            {Array.isArray(c.photos) && c.photos.length > 0 && (
                              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                                {c.photos.filter(Boolean).map((p, pi) => {
                                  const src = photoSrc(p);
                                  const lbIdx = taskPhotoLightboxIndex[idx]?.[pi];
                                  return (
                                    <button
                                      key={pi}
                                      type="button"
                                      onClick={() => {
                                        if (typeof lbIdx === "number") setLightboxIndex(lbIdx);
                                      }}
                                      className="relative rounded-md overflow-hidden border border-white/10 hover:border-[#1FB6C1]/60"
                                      data-testid={`portal-task-photo-${c.id || idx}-${pi}`}
                                    >
                                      <img
                                        src={src}
                                        alt=""
                                        className="w-full h-16 sm:h-14 object-cover"
                                        loading="lazy"
                                      />
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            {(c.comments || []).length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {c.comments.map((cm, i) => (
                                  <li
                                    key={cm.id || i}
                                    className="text-[12px] text-[#dde3ec] bg-black/20 rounded px-2 py-1"
                                  >
                                    <ExpandableText
                                      text={cm.text}
                                      lines={3}
                                      textClassName="whitespace-pre-wrap"
                                      testId={`portal-task-comment-${c.id}-${i}`}
                                    >
                                      <ReadAloudButton text={cm.text} size="xs" testId={`portal-task-comment-tts-${c.id}-${i}`} />
                                    </ExpandableText>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Photos */}
            {visiblePhotos.length > 0 && (
              <Section title={`Photos (${visiblePhotos.length})`}>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {visiblePhotos.map((p, i) => {
                    const src = photoSrc(p);
                    if (!src) return null;
                    return (
                      <button
                        key={p.id || i}
                        type="button"
                        onClick={() => setLightboxIndex(i)}
                        className="block aspect-square rounded overflow-hidden border border-white/10 hover:border-[#1FB6C1]/50 relative"
                        data-testid={`portal-jc-photo-${i}`}
                      >
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Documents */}
            {visibleDocs.length > 0 && (
              <Section title={`Documents (${visibleDocs.length})`}>
                <ul className="space-y-1.5 text-sm">
                  {visibleDocs.map((d, i) => (
                    <li key={d.id || i}>
                      <button
                        type="button"
                        onClick={() => downloadPortalDoc(d)}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded border border-white/5 hover:border-[#1FB6C1]/40 transition text-left"
                        data-testid={`portal-jc-doc-${i}`}
                      >
                        <FileText className="w-4 h-4 text-[#1FB6C1]" />
                        <span className="flex-1 truncate">
                          {d.label || d.filename || d.name || "Document"}
                        </span>
                        <Download className="w-3.5 h-3.5 text-[#647087]" />
                      </button>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Sign-off (customer only) */}
            {job.client_signoff || job.signatures?.client ? (
              <Section title="Customer sign-off">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                  <div>
                    <div className="font-medium">
                      {(job.client_signoff?.name || job.signatures?.client?.name) || "Signed"}
                    </div>
                    <div className="text-[11px] text-[#647087]">
                      {(job.client_signoff?.signed_at || job.signatures?.client?.at) &&
                        new Date(
                          job.client_signoff?.signed_at ||
                            job.signatures?.client?.at,
                        ).toLocaleString("en-GB")}
                    </div>
                  </div>
                </div>
              </Section>
            ) : !isStaffPreview && user?.role === "client" ? (
              // [Iter 348 — 2026-02-26] Customer sign-off + feedback
              // widget. Only rendered for the customer themselves (not
              // the staff preview lens — impersonation must NOT sign
              // on the customer's behalf) and only when the JC has not
              // yet been signed off. Posts to
              // `POST /api/portal/jobcards/{jid}/signoff` which stores
              // the signature + rating + feedback on the JC and pushes
              // a notification to every assigned technician.
              <ClientSignoffWidget
                jid={jid}
                defaultName={customer?.name || user?.name || ""}
                onSigned={(updated) => {
                  setJob(updated);
                  toast.success("Thanks — your feedback has been sent to the team.");
                }}
              />
            ) : null}

            {/* Customer rating */}
            {(job.rating_customer || job.rating_customer_notes) && (
              <Section title="Your rating">
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`w-5 h-5 ${
                        n <= (job.rating_customer || 0)
                          ? "fill-amber-400 text-amber-400"
                          : "text-[#475063]"
                      }`}
                    />
                  ))}
                  {job.rating_customer && (
                    <span className="ml-2 text-xs text-[#9aa3b5]">
                      {job.rating_customer}/5
                    </span>
                  )}
                </div>
                {job.rating_customer_notes && (
                  <p className="whitespace-pre-wrap text-[#dde3ec]">
                    {job.rating_customer_notes}
                  </p>
                )}
              </Section>
            )}

            {/* Actions footer */}
            <div className="border-t border-white/10 pt-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-[#647087] italic max-w-[60%]">
                Internal notes, costs and staff-only comments are filtered out
                of this view automatically.
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => downloadPdf()}
                  disabled={downloadingPdf}
                  className="hv-btn hv-btn-primary text-xs"
                  data-testid="portal-jc-pdf-bottom"
                >
                  {downloadingPdf ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing…
                    </>
                  ) : (
                    <>
                      <Download className="w-3.5 h-3.5" /> Download PDF
                    </>
                  )}
                </button>
                {/* [Iter 290zp.28] Removed the "Print" button — it fell
                    through to `window.print()`, which produced a
                    browser web-print of the on-screen React DOM
                    (missing branding, vehicle, signatures, checklist
                    photos). The Download PDF button now serves the
                    canonical WeasyPrint-rendered document. */}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* [Iter 345] In-portal image lightbox — keeps the portal shell
          intact when the customer taps a photo. Uses `credentials:
          include` so the /api/uploads/{fid} request carries the
          session cookie / bearer token. */}
      <MediaLightbox
        items={lightboxItems}
        index={lightboxIndex}
        onChange={setLightboxIndex}
        onClose={() => setLightboxIndex(-1)}
      />
    </div>
  );
}

function FactCard({ icon: Icon, label, value }) {
  return (
    <div className="hv-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#647087]">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}

function Section({ title, children, action = null }) {
  return (
    <section className="hv-card p-5">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2 className="font-display text-base font-bold text-white">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}


/**
 * [Iter 348 — 2026-02-26] Customer sign-off + rating + feedback widget.
 *
 * Rendered on the portal JC page only when:
 *   (a) the JC has NOT yet been signed off by the customer, and
 *   (b) the current viewer is the real customer (`role === "client"`)
 *       — staff in preview lens are NOT allowed to sign on the
 *       customer's behalf.
 *
 * Posts to `POST /api/portal/jobcards/{jid}/signoff` with:
 *   { name, signature_data_url, rating, feedback }
 *
 * The rating (1-5) is INTERNAL — visible in the CRM only, never on
 * the customer PDF (Dirk 2026-02-26: "PDF must not show ratings").
 * Feedback text DOES render on the PDF as a "Customer feedback" block
 * so the customer's own words reach the document their manager sees.
 * Signature is required.
 *
 * After success the backend fires a system bubble on the JC's chat
 * thread AND a Web Push to every assigned technician + CC managers.
 */
function ClientSignoffWidget({ jid, defaultName, onSigned }) {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (sigDataUrl, typedName) => {
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/portal/jobcards/${jid}/signoff`, {
        name: (typedName || defaultName || "").trim(),
        signature_data_url: sigDataUrl,
        rating: rating || null,
        feedback: feedback.trim() || null,
      });
      onSigned?.(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Sign-off failed.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="hv-card p-5 border-2 border-[#1FB6C1]/30 bg-[#1FB6C1]/[0.03]"
      data-testid="portal-jc-signoff-widget"
    >
      <div className="flex items-center gap-2 mb-1">
        <CheckCircle2 className="w-5 h-5 text-[#1FB6C1]" />
        <h2 className="font-display text-base font-bold text-white">
          Confirm the visit
        </h2>
      </div>
      <p className="text-xs text-[#9aa3b5] mb-4">
        Please rate your experience, leave any feedback for the team, then sign
        below to close off this job.
      </p>

      {/* Star rating */}
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider text-[#647087] mb-2">
          Rate the visit (optional)
        </div>
        <div className="flex items-center gap-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n === rating ? 0 : n)}
              className={`w-10 h-10 rounded border transition ${
                n <= rating
                  ? "border-amber-400 bg-amber-400/20 text-amber-300"
                  : "border-white/10 text-[#647087] hover:text-amber-200 hover:border-amber-400/40"
              }`}
              data-testid={`portal-jc-signoff-star-${n}`}
              aria-label={`Rate ${n} stars`}
            >
              <Star
                className={`w-5 h-5 mx-auto ${
                  n <= rating ? "fill-amber-400 text-amber-400" : ""
                }`}
              />
            </button>
          ))}
          {rating > 0 && (
            <span className="ml-2 text-xs text-[#dde3ec]">{rating}/5</span>
          )}
        </div>
      </div>

      {/* Feedback textarea */}
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider text-[#647087] mb-2">
          Feedback for the team (optional)
        </div>
        <DictTextarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value.slice(0, 2000))}
          placeholder="What went well? Anything that could be better?"
          rows={3}
          maxLength={2000}
          className="w-full rounded border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-[#647087] focus:border-[#1FB6C1] focus:outline-none"
          data-testid="portal-jc-signoff-feedback"
        />
        <div className="text-[10px] text-[#647087] mt-1 text-right">
          {feedback.length} / 2000
        </div>
      </div>

      {/* Signature pad */}
      <SignaturePad
        onSave={(dataUrl, typedName) => submit(dataUrl, typedName)}
        label="Sign here to confirm the job is complete"
        testIdPrefix="portal-jc-signoff-pad"
        nameField
        nameLabel="Print name (person signing)"
        defaultName={defaultName}
      />

      {submitting && (
        <div className="mt-3 text-xs text-[#9aa3b5] flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending your sign-off…
        </div>
      )}
      {err && (
        <div className="mt-3 text-xs text-rose-300 flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5" /> {err}
        </div>
      )}
    </section>
  );
}
